import { ModalInput } from "components/atoms/input/modal/ModalInput";
import styled from "styled-components";
import { calcRem } from "styles/theme";
import { COLORS } from "styles/colors";
import { FLEX } from "styles/flex";
import { Row } from "styles/ModalCommonStyled";
import {
  ActivateKey,
  MergeableFreeKeys,
  SUBSCRIPTION_TYPE,
} from "api/interfaces/subscription.interface";
import { formatNumber } from "utils/functions";
import { getAccountTimeFormatDate } from "utils/timeUtil";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useAppSelector } from "redux/hooks";
import { ReactComponent as LONG_ARROW } from "assets/icons/long_arrow.svg";
import * as mediaQuery from "components/MediaQuery";
import { FaLongArrowAltDown } from "react-icons/fa";
import { InputNumber } from "antd";
import moment from "moment";
import { useMemo } from "react";
import StorageSlider from "../StorageSlider";
type Props = {
  data: ActivateKey;
  userFixedStorage?: number;
  onChangeStorage: (number?: number | null) => void;
  selectedMergeFreeKey?: MergeableFreeKeys;
};

export default function ConfirmActivateBottomInfo(props: Props): JSX.Element {
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const { data, userFixedStorage, selectedMergeFreeKey } = props;

  const mergedExpireDate = useMemo(
    () =>
      selectedMergeFreeKey
        ? moment
            .utc(data.nextExpire)
            .add(selectedMergeFreeKey.remainingDays, "days")
            .toISOString()
        : undefined,
    [data.nextExpire, selectedMergeFreeKey]
  );

  return (
    <BottomWrapper>
      <ExpireRow>
        <ModalInput
          label={
            data.keyType === SUBSCRIPTION_TYPE.STANDARD
              ? "Required"
              : "Activated Storage (TB)"
          }
          noBold
        >
          {data.keyType === SUBSCRIPTION_TYPE.STANDARD && (
            <ChannelRow>
              <h2>{formatNumber(data.registeredChannel)}</h2>
            </ChannelRow>
          )}

          {data.keyType === SUBSCRIPTION_TYPE.STORAGE && (
            <StorageRow>
              <InputNumber
                value={userFixedStorage}
                onChange={props.onChangeStorage}
                max={data.allocated}
                min={data.preActivated}
              />
              <StorageSlider
                value={userFixedStorage}
                max={data.allocated}
                min={data.preActivated}
                onChange={props.onChangeStorage}
              />
            </StorageRow>
          )}
        </ModalInput>
      </ExpireRow>
      <ExpireGroup>
        <ExpireRow>
          <ModalInput label="Expiration" noBold>
            <h2 className="strike-text date">
              {getAccountTimeFormatDate(data.preExpire, {
                ...selectedAccount,
                timezone: "UTC",
              })}
            </h2>
          </ModalInput>
          <mediaQuery.Default>
            <LONG_ARROW className="arrow-svg" />
          </mediaQuery.Default>
          <mediaQuery.Mobile>
            <FaLongArrowAltDown />
          </mediaQuery.Mobile>
          <ModalInput label="">
            <h2 className="date">
              {getAccountTimeFormatDate(data.nextExpire, {
                ...selectedAccount,
                timezone: "UTC",
              })}
            </h2>
          </ModalInput>
        </ExpireRow>
        {selectedMergeFreeKey && mergedExpireDate && (
          <MergeExpireInfo>
            <span className="days">
              + {selectedMergeFreeKey.remainingDays} Days
            </span>
            <span className="merged-date">
              {getAccountTimeFormatDate(mergedExpireDate, {
                ...selectedAccount,
                timezone: "UTC",
              })}
            </span>
          </MergeExpireInfo>
        )}
      </ExpireGroup>
    </BottomWrapper>
  );
}

const BottomWrapper = styled.div`
  width: 100%;
  background-color: ${({ theme }) => theme.COLORS.PRIMARY3};
  padding: 20px 30px;
  margin-top: 16px;
  ${FLEX.FlexStartStart};
  flex-direction: column;
  gap: 10px;
  color: ${({ theme }) => theme.COLORS.WHITE};

  .strike-wrapper {
    display: flex;
    align-items: center;
  }

  .strike-text {
    position: relative;
    color: ${({ theme }) => theme.COLORS.WHITE};
    text-decoration: line-through;
    text-decoration-thickness: 3px;
  }

  .strike-line {
    flex-grow: 1;
    height: 3px;
    width: 55px;
    background-color: ${({ theme }) => theme.COLORS.WHITE};
    margin-left: -3px;
  }

  .arrow-svg {
    margin-left: -12px;
  }

  h2 {
    font-size: ${calcRem(32)};
  }
`;

const ArrowRow = styled(Row)`
  position: relative;

  .arrow-svg {
    ${mediaQuery.isDefault} {
      top: 65%;
      left: 43%;
      position: absolute;
      transform: translate(-50%, -50%);
    }
  }
`;

const ChannelRow = styled(ArrowRow)`
  h2 {
    flex: 1;
    position: relative;
    .arrow-svg {
      ${mediaQuery.isDefault} {
        top: 50%;
        right: 0;
        left: unset;
        margin-left: 0;
        /* left: 0; */
        /* transform: translateX(0); */
      }
    }
  }
  ${mediaQuery.isMobile} {
    flex-direction: row;
  }
`;

const ExpireRow = styled(ArrowRow)`
  ${mediaQuery.isDefault} {
    align-items: flex-end;
  }
  ${mediaQuery.isMobile} {
    margin: 0;
  }
`;

const ExpireGroup = styled.div`
  width: 100%;
  ${FLEX.FlexStartStart};
  flex-direction: column;
  gap: 6px;
`;

const MergeExpireInfo = styled.div`
  width: 100%;
  ${FLEX.FlexStartEnd};
  flex-direction: column;
  gap: 4px;
  color: ${({ theme }) => theme.COLORS.WHITE};

  .days {
    font-size: ${calcRem(14)};
    color: ${COLORS.BUTTONS1};
  }

  .merged-date {
    font-size: ${calcRem(24)};
    font-weight: 500;
  }
`;

const StorageRow = styled.div`
  width: 100%;
  gap: 20px;
  ${FLEX.FlexBetweenCenter}

  .storage-slider-wrapper {
    flex: 1;
  }

  ${mediaQuery.isMobile} {
    flex-direction: column;
    align-items: flex-start;
    .storage-slider-wrapper {
      width: 100%;
    }
  }
`;
